import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, Gift, Loader2, Check, MessageCircle } from "lucide-react";
import { shopApi, formatPrice, formatApiError, resolveImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { useStore } from "@/context/StoreContext";
import { AirplaneLogo } from "@/components/motifs";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user, loading } = useCustomer();
  const { settings } = useStore();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [addr, setAddr] = useState({ label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
  const [gift, setGift] = useState({ is_gift: false, message: "", hide_pricing: false, recipient_name: "" });
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCode, setAppliedCode] = useState("");
  const [payMethod, setPayMethod] = useState("whatsapp");
  const [razorpayOn, setRazorpayOn] = useState(false);

  useEffect(() => {
    shopApi.get("/payments/config").then(({ data }) => setRazorpayOn(!!data.razorpay_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) shopApi.get("/customer/addresses").then(({ data }) => {
      setAddresses(data);
      const def = data.find((a) => a.is_default) || data[0];
      if (def) setAddr(def);
    });
  }, [user]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-amber-600" /></div>;

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-stone-900">Please log in to check out</h1>
        <p className="mt-2 text-stone-500">Create an account to place your order & track it.</p>
        <button data-testid="checkout-login" onClick={() => navigate("/account")} className="mt-6 rounded-full gold-gradient px-6 py-3 font-semibold uppercase tracking-wider text-stone-900">Login / Register</button>
      </div>
    );
  }

  if (placed) {
    const wa = settings?.whatsapp_number || "919999999999";
    let msg = `✈️ *PRIYARAA Order ${placed.order_number}*\n\n`;
    placed.items.forEach((i, x) => { msg += `${x + 1}. ${i.name} × ${i.qty}${gift.hide_pricing ? "" : ` — ${formatPrice(i.price * i.qty)}`}\n`; });
    if (!gift.hide_pricing) msg += `\n*Total: ${formatPrice(placed.total)}*\n`;
    msg += `\nName: ${placed.address.full_name}\nAddress: ${placed.address.line1}, ${placed.address.city} - ${placed.address.pincode}\nPhone: ${placed.address.phone}`;
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center" data-testid="order-success">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full gold-gradient"><Check size={30} className="text-stone-900" /></div>
        <h1 className="mt-5 font-serif text-4xl text-stone-900">Order Confirmed!</h1>
        <p className="mt-2 text-stone-600">Your order <span className="font-mono font-semibold">{placed.order_number}</span> is booked for its first-class flight.</p>
        <div className="mt-8 flex flex-col gap-3">
          <a data-testid="confirm-whatsapp" href={`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 font-medium text-white">
            <MessageCircle size={18} /> Confirm & Pay via WhatsApp
          </a>
          <button data-testid="go-track" onClick={() => navigate(`/track?o=${placed.order_number}`)} className="rounded-full border border-[#E7DFD3] py-3 text-sm font-medium text-stone-700 hover:border-amber-500">Track my order</button>
          <button onClick={() => navigate("/account")} className="text-sm text-stone-500 underline">View in my account</button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-stone-900">Your bag is empty</h1>
        <button onClick={() => navigate("/shop")} className="mt-6 rounded-full gold-gradient px-6 py-3 font-semibold uppercase tracking-wider text-stone-900">Shop the collection</button>
      </div>
    );
  }

  const set = (k, v) => setAddr((s) => ({ ...s, [k]: v }));
  const setG = (k, v) => setGift((s) => ({ ...s, [k]: v }));

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await shopApi.post("/coupons/validate", { code: coupon, subtotal: total, product_ids: items.map((i) => i.id) });
      setDiscount(data.discount); setAppliedCode(data.code);
      toast.success(`Coupon ${data.code} applied — ${formatPrice(data.discount)} off`);
    } catch (e) {
      setDiscount(0); setAppliedCode("");
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid coupon");
    }
  };

  const placeOrder = async () => {
    if (!addr.full_name || !addr.phone || !addr.line1 || !addr.city || !addr.pincode) {
      toast.error("Please fill your delivery address"); return;
    }
    setBusy(true);
    try {
      const { data } = await shopApi.post("/orders", {
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        address: addr, gift, payment_method: payMethod, coupon_code: appliedCode,
      });
      setPlaced(data);
      clear();
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not place order");
    }
    setBusy(false);
  };

  const payable = Math.max(0, total - discount);
  const shipping = payable >= 999 ? 0 : 49;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2"><AirplaneLogo size={30} /><h1 className="font-serif text-3xl text-stone-900">Checkout</h1></div>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          {/* address */}
          <div className="rounded-2xl border border-[#E7DFD3] bg-white p-5">
            <h2 className="font-serif text-xl text-stone-900">Delivery Address</h2>
            {addresses.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {addresses.map((a) => (
                  <button key={a.id} onClick={() => setAddr(a)} className={`rounded-lg border px-3 py-2 text-left text-xs ${addr.id === a.id ? "border-amber-500 bg-amber-50" : "border-[#E7DFD3]"}`}>
                    <span className="font-medium">{a.label}</span><br />{a.line1}, {a.city}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <input data-testid="co-name" placeholder="Full name" value={addr.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
              <input data-testid="co-phone" placeholder="Phone" value={addr.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
              <input data-testid="co-line1" placeholder="Address line 1" value={addr.line1} onChange={(e) => set("line1", e.target.value)} className="col-span-2 rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
              <input data-testid="co-city" placeholder="City" value={addr.city} onChange={(e) => set("city", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
              <input data-testid="co-pincode" placeholder="Pincode" value={addr.pincode} onChange={(e) => set("pincode", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>

          {/* gift */}
          <div className="rounded-2xl border border-[#E7DFD3] bg-white p-5">
            <label className="flex items-center gap-2 font-serif text-xl text-stone-900">
              <input type="checkbox" data-testid="gift-toggle" checked={gift.is_gift} onChange={(e) => setG("is_gift", e.target.checked)} className="h-4 w-4 accent-amber-600" />
              <Gift size={18} className="text-amber-700" /> Make it a gift
            </label>
            {gift.is_gift && (
              <div className="mt-4 space-y-3">
                <input data-testid="gift-recipient" placeholder="Recipient's name" value={gift.recipient_name} onChange={(e) => setG("recipient_name", e.target.value)} className="w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
                <textarea data-testid="gift-message" rows={2} placeholder="Personalised gift message" value={gift.message} onChange={(e) => setG("message", e.target.value)} className="w-full resize-none rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
                <label className="flex items-center gap-2 text-sm text-stone-600">
                  <input type="checkbox" data-testid="gift-hide-price" checked={gift.hide_pricing} onChange={(e) => setG("hide_pricing", e.target.checked)} className="h-4 w-4 accent-amber-600" />
                  Hide prices on the invoice inside the parcel
                </label>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#F4E7C5]/50 p-3">
              <Plane size={16} className="text-amber-700" />
              <p className="font-mono text-[10px] uppercase tracking-wide text-amber-800">Signature golden passport gift box included free</p>
            </div>
          </div>
        </div>

        {/* summary */}
        <div className="h-fit rounded-2xl border border-[#E7DFD3] bg-white p-5">
          <h2 className="font-serif text-xl text-stone-900">Order Summary</h2>
          <div className="mt-4 space-y-3">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-3">
                <img src={resolveImg(i.image_url)} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
                <div className="flex-1"><p className="text-sm text-stone-800">{i.name}</p><p className="text-xs text-stone-500">Qty {i.qty}</p></div>
                <span className="text-sm font-medium">{formatPrice(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-[#EFE8DC] pt-4 text-sm">
            <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{formatPrice(total)}</span></div>
            <div className="flex items-center gap-2 py-1">
              <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="flex-1 rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-sm outline-none focus:border-amber-500" />
              <button data-testid="coupon-apply" onClick={applyCoupon} className="rounded-lg border border-amber-500 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50">Apply</button>
            </div>
            {discount > 0 && <div className="flex justify-between text-green-700" data-testid="checkout-discount"><span>Discount ({appliedCode})</span><span>−{formatPrice(discount)}</span></div>}
            <div className="flex justify-between text-stone-600"><span>Shipping</span><span>{shipping === 0 ? "FREE" : formatPrice(shipping)}</span></div>
            <div className="flex justify-between pt-2 font-serif text-lg text-stone-900"><span>Total</span><span data-testid="checkout-total">{formatPrice(payable + shipping)}</span></div>
          </div>
          <div className="mt-5" data-testid="payment-methods">
            <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Payment</p>
            <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-amber-400 bg-amber-50/50 px-3 py-2">
              <input type="radio" name="pay" data-testid="pay-whatsapp" checked={payMethod === "whatsapp"} onChange={() => setPayMethod("whatsapp")} className="accent-amber-600" />
              <span className="text-sm text-stone-800">Confirm &amp; pay via WhatsApp</span>
            </label>
            <label className={`mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 ${razorpayOn ? "cursor-pointer border-[#E7DFD3]" : "cursor-not-allowed border-dashed border-[#E7DFD3] opacity-60"}`}>
              <input type="radio" name="pay" data-testid="pay-online" disabled={!razorpayOn} checked={payMethod === "online"} onChange={() => setPayMethod("online")} className="accent-amber-600" />
              <span className="text-sm text-stone-800">Pay Online — UPI / Card / Netbanking {razorpayOn ? "" : "(activate in Admin with Razorpay keys)"}</span>
            </label>
          </div>
          <button data-testid="place-order-button" onClick={placeOrder} disabled={busy} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-3.5 font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plane size={17} />} Place Order
          </button>
          <p className="mt-2 text-center text-[11px] text-stone-400">Payment confirmed over WhatsApp after you place the order.</p>
        </div>
      </div>
    </div>
  );
}
